<template>
  <div class="container inline-flex justify-between text-sm rounded-xl bg-white px-3 py-1">
    <div class="block col-md-6 font-medium text-gray-700">
      <ul ref="liItems">
        <li class="mt-4 mb-5 flex items-center" :key="0" ref="liIcon">
          <CheckRequirementsNetwork
            @click="toggleTooltip(0)"
            class="relative mr-1"
            :isSuccess="speedCheckPassed(downloadSpeed)"
          />
          Download speed (approx)
          <p
            class="text-red-600 error-tip"
            v-if="!speedCheckPassed(downloadSpeed) && downloadSpeed"
          >
            Your download speed is too low
          </p>
          <div class="tooltip" v-if="activeTooltip === 0">20 Mbps or up</div>
        </li>
        <li class="mt-5 mb-5 flex items-center" :key="1" ref="liIcon">
          <CheckRequirementsNetwork
            @click="toggleTooltip(1)"
            class="relative mr-1"
            :isSuccess="speedCheckPassed(uploadSpeed, 'upload')"
          ></CheckRequirementsNetwork>
          Upload speed (approx)
          <p
            class="text-red-600 error-tip"
            v-if="!speedCheckPassed(uploadSpeed, 'upload') && uploadSpeed"
          >
            Your upload speed is too low
          </p>
          <div class="tooltip" v-if="activeTooltip === 1">
            {{ isClinician ? '20' : '15' }} Mbps or up
          </div>
        </li>
        <li class="mt-5 mb-5 flex items-center" :key="2" ref="liIcon">
          <CheckRequirementsBrowser
            @click="toggleTooltip(2)"
            class="relative mr-1"
            :isSuccess="this.browserCheck"
          />
          Browser
          <p class="text-red-600 error-tip" style="margin-top: 2.7rem" v-if="!this.browserCheck">
            Your browser is not supported.
            <br />
            Please download the latest version of
            <a
              href="https://www.google.com/intl/en/chrome/"
              target="_blank"
              class="outline-none text-blue-600 dark:text-blue-500 hover:underline"
            >
              Chrome
            </a>
            /
            <a
              href="https://www.microsoft.com/en-us/edge"
              target="_blank"
              class="outline-none text-blue-600 dark:text-blue-500 hover:underline"
            >
              Edge
            </a>
          </p>
          <div class="tooltip" v-if="activeTooltip === 2">Google Chrome or Microsoft Edge</div>
        </li>
        <li class="mt-5 mb-5 flex items-center" :key="3" ref="liIcon">
          <CheckRequirementsMonitor class="relative mr-1" :isSuccess="serverConnection" />
          <span v-if="serverConnection">Connection to our servers OK</span>
          <span v-else>None or partial connection to our servers</span>
        </li>
        <li class="mt-5 mb-5 flex items-center" :key="4" ref="liIcon">
          <CheckRequirementsMonitor class="relative mr-1" :isSuccess="webRtcCheck" />
          {{
            webRtcCheck
              ? 'Optimal media connection confirmed'
              : 'Fallback mode active; audio or video clarity may be reduced'
          }}
        </li>
      </ul>
    </div>

    <div class="block col-md-6 text-right text-gray-600">
      <ul>
        <li class="mt-6 mb-4">
          <span v-if="downloadSpeed">
            {{ `${downloadSpeed} Mbps` }}
          </span>
          <span v-else class="inline-flex">
            <svg
              class="animate-spin -ml-1 mr-5 h-3 w-3"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="#14B8A6"
                stroke-width="4"
              ></circle>
              <path
                fill="#14B8A6"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </span>
        </li>
        <li class="mt-2 mb-4 li-measured">
          <span v-if="uploadSpeed">
            {{ `${uploadSpeed} Mbps` }}
          </span>
          <span v-else class="inline-flex">
            <svg
              class="animate-spin -ml-1 mr-5 h-3 w-3"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="#14B8A6"
                stroke-width="4"
              ></circle>
              <path
                fill="#14B8A6"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </span>
        </li>
        <li class="mt-2 mb-2 li-measured">{{ getBrowserName() }}</li>
        <li class="mt-2 mb-4 li-measured flex justify-end">
          <ViceWelcomeTestConnection :result="serverConnection" :running="isRunning" />
        </li>
        <li class="mt-2 mb-4 li-measured flex justify-end">
          <ViceWelcomeTestConnection :result="webRtcCheck" :running="isRunning" />
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import {
  APP_URL,
  CHAT_DOMAIN,
  DEVICE_SCREEN_SHARE_APP_DOMAIN,
  DEVICE_WEBSOCKET_DOMAIN,
  DEVICE_WEBSOCKET_PORT,
  IOT_WEBSOCKET_DOMAIN,
  JITSI_SERVER_DOMAIN,
  LARAVEL_ECHO_SERVER_HOST,
  LARAVEL_ECHO_SERVER_PORT,
  LARAVEL_ECHO_SERVER_SCHEME,
  TEST_IMAGE_CDN_URL,
  TEST_IMAGE_SIZE,
  TURN_CREDENTIALS,
} from '@/Utils/vice-constants';
import CheckRequirementsNetwork from '@/Components/Icons/CheckRequirementsNetwork.vue';
import CheckRequirementsBrowser from '@/Components/Icons/CheckRequirementsBrowser.vue';
import CheckRequirementsMonitor from '@/Components/Icons/CheckRequirementsMonitor.vue';
import TestHostsAndPorts from '@/Components/Modals/TestHostsAndPorts.vue';
import ViceWelcomeTestConnection from '@/Components/Icons/ViceWelcomeTestConnection.vue';
import useSessionStore from '@/Stores/useSessionStore';

const DEFAULT_PORT = 443;
const DEFAULT_SCHEME = 'https';

export default {
  name: 'ConnectionPrecheck',
  props: {
    isClinician: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    ViceWelcomeTestConnection,
    CheckRequirementsMonitor,
    CheckRequirementsBrowser,
    CheckRequirementsNetwork,
  },
  data() {
    const sessionStore = useSessionStore();
    return {
      sessionStore,
      isOpen: true,
      browserName: null,
      downloadSpeed: null,
      uploadSpeed: null,
      activeTooltip: null,
      activeInfoTooltip: false,
      checkOS: false,
      browserCheck: false,
      webRtcCheck: true,
      serverConnection: true,
      isRunning: false,
      hosts: [
        {
          scheme: LARAVEL_ECHO_SERVER_SCHEME,
          host: LARAVEL_ECHO_SERVER_HOST,
          port: process.env.NODE_ENV === 'production' ? DEFAULT_PORT : LARAVEL_ECHO_SERVER_PORT,
          purpose: 'Websocket server',
          status: '',
          class: null,
          passed: false,
        },
        {
          scheme: DEFAULT_SCHEME,
          host: JITSI_SERVER_DOMAIN,
          port: DEFAULT_PORT,
          purpose: 'Video conference server',
          status: '',
          class: null,
          passed: false,
        },
        {
          scheme: DEFAULT_SCHEME,
          host: CHAT_DOMAIN,
          port: DEFAULT_PORT,
          purpose: 'VCE chat service',
          status: '',
          class: null,
          passed: false,
        },
        {
          scheme: 'wss',
          host: DEVICE_WEBSOCKET_DOMAIN,
          port: DEVICE_WEBSOCKET_PORT, // todo: replace with 443 by Nginx proxy
          purpose: 'Android signaling server',
          status: '',
          class: null,
          passed: false,
        },
        {
          scheme: 'wss',
          host: IOT_WEBSOCKET_DOMAIN,
          port: DEFAULT_PORT,
          purpose: 'IOT Websocket Client',
          status: '',
          class: null,
          passed: false,
        },
      ],
    };
  },
  mounted() {
    this.checkSystem();
  },
  emits: ['togglePlatformRequirementsModal'],
  methods: {
    serverConnectionCheck(hasPassed) {
      this.serverConnection = hasPassed;
    },

    toggleTooltip(index) {
      if (this.activeTooltip === index) {
        this.activeTooltip = null;
      } else {
        this.activeTooltip = index;
      }

      if (index === 'info') {
        this.activeInfoTooltip = !this.activeInfoTooltip;
      }
    },

    closeModal() {
      this.$emit('togglePlatformRequirementsModal', false);
    },

    async checkSystem() {
      this.browserName = this.getBrowserName();
      this.checkServerConnection(this.hosts);
      await this.checkDownloadSpeed();
      await this.checkUploadSpeed();
      await this.checkWebRTCConnection();
    },

    async checkServerConnection() {
      const report = await TestHostsAndPorts.methods.shortCheckAllResources(this.hosts);
      const passedValues = report.map((check) => check.passed);
      const hasFailed = passedValues.some((value) => value === false);

      this.serverConnection = !hasFailed;
    },

    async checkWebRTCConnection() {
      this.sessionStore.blockedUDP = true;

      return new Promise((resolve) => {
        this.webRtcCheck = false;
        this.sessionStore.blockedUDP = true;

        const peerConnection = new RTCPeerConnection({
          iceServers: [
            {
              urls: `turn:${DEVICE_SCREEN_SHARE_APP_DOMAIN}:3478?transport=udp`,
              username: TURN_CREDENTIALS,
              credential: TURN_CREDENTIALS,
            },
          ],
          iceTransportPolicy: 'relay',
        });

        peerConnection.onicecandidate = (event) => {
          if (!event.candidate) return;

          const c = event.candidate;

          const isRelay = c.type === 'relay';
          const isUdp =
            c.relayProtocol === 'udp' ||
            c.protocol === 'udp' ||
            c.url?.includes('transport=udp') ||
            (!c.url?.includes('transport=tcp') && !c.url?.includes('turns:'));

          if (isRelay && isUdp) {
            this.webRtcCheck = true;
            this.sessionStore.blockedUDP = false;

            peerConnection.close();
            resolve();
          }
        };

        peerConnection.createDataChannel('data');
        peerConnection
          .createOffer()
          .then((offer) => peerConnection.setLocalDescription(offer))
          .catch((error) => {
            console.error('Error creating offer: ', error);
          });
      });
    },

    async checkUploadSpeed() {
      const http = new XMLHttpRequest();
      let endTime;
      const uploadUrl = `${APP_URL}/check-upload`;
      const data = `d=${'x'.repeat(10 * 1024 * 1024)}`;

      http.open('POST', uploadUrl, true);
      http.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
      http.onreadystatechange = () => {
        if (http.readyState === 4 && http.status === 200) {
          endTime = performance.now();
          this.uploadSpeed = getResult();
        }
      };

      const startTime = performance.now();
      http.send(data);

      const getResult = () => {
        const duration = (endTime - startTime) / 1000;
        const bitsLoaded = data.length * 8;

        return (bitsLoaded / duration / 1000000).toFixed(2);
      };
    },

    async checkDownloadSpeed() {
      let endTime;
      const download = new Image();
      const downloadSize = TEST_IMAGE_SIZE;
      const downloadUrl = TEST_IMAGE_CDN_URL;

      download.onload = () => {
        endTime = performance.now();
        this.downloadSpeed = getResult();
      };

      const startTime = performance.now();
      download.src = `${downloadUrl}?no-cache=${startTime}`;

      const getResult = () => {
        const duration = (endTime - startTime) / 1000;
        const bitsLoaded = downloadSize * 8;

        return (bitsLoaded / duration / 1000 / 1000).toFixed(2);
      };
    },

    speedCheckPassed(speed, type = 'download') {
      let minSpeed = 20;
      if (type === 'upload' && !this.isClinician) {
        minSpeed = 15;
      }

      return speed > minSpeed;
    },

    getBrowserName() {
      const userAgent = navigator.userAgent;
      let browserName = 'Unknown';

      if (userAgent.indexOf('Firefox') !== -1 || userAgent.indexOf('FxiOS') !== -1) {
        browserName = 'Mozilla Firefox';
      } else if (userAgent.indexOf('Opera') !== -1 || userAgent.indexOf('OPR') !== -1) {
        browserName = 'Opera';
      } else if (userAgent.indexOf('Edge') !== -1 || userAgent.indexOf('Edg') !== -1) {
        this.browserCheck = true;
        browserName = 'Microsoft Edge';
      } else if (userAgent.indexOf('Chrome') !== -1 || userAgent.indexOf('CriOS') !== -1) {
        this.browserCheck = true;
        browserName = 'Google Chrome';
      } else if (userAgent.indexOf('Safari') !== -1) {
        browserName = 'Apple Safari';
      } else if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident/') !== -1) {
        browserName = 'Internet Explorer';
      }
      return browserName;
    },
  },
};
</script>

<style scoped>
.tooltip {
  position: absolute;
  background: #262626;
  color: white;
  font-size: smaller;
  border-radius: 0 5px 5px 5px;
  padding-left: 5px;
  padding-right: 5px;
  margin-top: 2.5rem;
  margin-left: 2.5rem;
}

.tooltip-info ul {
  list-style: inside;
  line-height: 1.3rem;
}

.li-measured {
  margin-top: 2.2rem;
}

.shadow {
  background-color: rgba(238, 238, 238, 100);
}

.error-tip {
  line-height: 0.8rem;
  position: absolute;
  margin: 2rem 0 0 2.5rem;
  font-size: smaller;
}
</style>
